/* eslint-disable no-constant-condition */
import {
  Box,
  Typography,
  Stack,
  Paper,
  useTheme,
  Card,
  Avatar,
  Skeleton,
  Menu,
  MenuItem,
  IconButton,
  Divider
} from '@mui/material';
import { IoMdCard } from 'react-icons/io';
import UsageProgressCard from '@/components/Layouts/components/UsageProgressCard';
import Button from '@/components/Common/Button';
import {
  useAddCardDetails,
  useGetBusinessBillingHistory,
  useGetBusinessPlan,
  useGetCardDetails
} from '@/apis/business/setting/billing/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { formatDollars, formatNumberDecimals } from '@/utils/helperFunctions';
import { formatPhoneNumberChargesQuantityLabel } from '@/utils/phoneNumberBilling';
import Grid from '@mui/material/Grid2';
import { useState } from 'react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { GoCircle, GoPlus } from 'react-icons/go';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { formatDuration } from 'utils/helperFunctions';
import { useDeleteCard, useUpdateCardDetails } from '@/apis/business/setting/billing/hook';
import { FaDollarSign } from 'react-icons/fa6';
import ApplyCoupon from './ApplyCoupon';
import { getCardBrandImage } from '@/pages/Agency/Billing/components/BillingDetails';

const PaymentMethod = () => {
  const theme = useTheme();
  const { data: businessProfile } = useGetBusinessProfile();
  const { mutate: addCard, isPending: addingCard } = useAddCardDetails();
  const { data: cardDetails, isLoading } = useGetCardDetails({
    enabled: Boolean(businessProfile?.agency_billing_enabled)
  });
  const { mutateAsync: deleteCard } = useDeleteCard();
  const { mutateAsync: updateCard } = useUpdateCardDetails();

  const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement | null; cardId: string | null }>({
    anchorEl: null,
    cardId: null
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, cardId: string) => {
    setMenuState({ anchorEl: event.currentTarget, cardId });
  };

  const handleMenuClose = () => {
    setMenuState({ anchorEl: null, cardId: null });
  };

  if (isLoading) return <PaymentMethodSkeleton />;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box
          display='flex'
          flexDirection={{ xs: 'column', lg: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          gap={{ xs: 2, lg: 0 }}
        >
          <Box>
            <Typography variant='subtitle1' fontWeight={500} fontSize={20}>
              Payment Method
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              fontSize={16}
              mt={0.5}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              Connect your payment method to manage your subscription
            </Typography>
          </Box>
          {Boolean(cardDetails?.data?.length) && (
            <Button
              onClick={() => addCard()}
              variant='contained'
              color='primary'
              startIcon={<GoPlus />}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              Add Card
            </Button>
          )}
        </Box>

        {cardDetails?.data?.length ? (
          <>
            {cardDetails?.data?.map((cardDetail: any) => (
              <Card
                key={cardDetail?.last4}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  borderRadius: '12px',
                  bgcolor: '#f9fafb',
                  boxShadow: 'none',
                  p: { xs: 1.5, lg: 2 },
                  gap: { xs: 1, sm: 0 }
                }}
              >
                <Box display='flex' alignItems='center' gap={2} flex={1} width='100%'>
                  {getCardBrandImage(theme, cardDetail.type === 'link' ? 'link' : cardDetail.display_brand)}
                  {cardDetail.type === 'link' ? (
                    <Typography fontWeight={500}>{cardDetail.email}</Typography>
                  ) : (
                    <Box flex={1} minWidth={0}>
                      <Box
                        display='flex'
                        alignItems='center'
                        gap={1}
                        flexWrap='wrap'
                        sx={{
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' }
                        }}
                      >
                        <Typography fontWeight={500}>**** **** **** {cardDetail?.last4}</Typography>
                        {cardDetail?.default && (
                          <Typography
                            px={1}
                            py={0.2}
                            sx={{ background: theme.palette.primary.light, borderRadius: 5 }}
                            color='primary'
                            fontSize={14}
                            fontWeight={500}
                          >
                            Default
                          </Typography>
                        )}
                      </Box>
                      <Typography variant='body2' color='text.secondary'>
                        Expires {cardDetail?.exp_month}/{cardDetail?.exp_year}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {!cardDetail?.default && (
                  <>
                    <IconButton
                      color='primary'
                      edge='start'
                      onClick={(e) => handleMenuOpen(e, cardDetail.id)}
                      sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}
                    >
                      <BiDotsVerticalRounded />
                    </IconButton>
                    <Menu
                      anchorEl={menuState.anchorEl}
                      open={menuState.cardId === cardDetail.id}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                      slotProps={{ paper: { sx: { width: 205, boxShadow: '0px 2px 8px rgba(0,0,0,0.12)' } } }}
                    >
                      <MenuItem
                        onClick={() => {
                          updateCard(cardDetail?.id);
                          handleMenuClose();
                        }}
                      >
                        <GoCircle style={{ marginRight: 8 }} />
                        Set as Default
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          deleteCard(cardDetail?.id);
                          handleMenuClose();
                        }}
                        sx={{ color: 'red' }}
                      >
                        <RiDeleteBin6Line style={{ marginRight: 8, color: 'red' }} />
                        Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Card>
            ))}
          </>
        ) : (
          <Box
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            p={{ xs: 1.5, lg: 2 }}
            gap={{ xs: 2, sm: 0 }}
            sx={{
              border: '1px dashed #ddd',
              borderRadius: 2
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center' flex={1}>
              <Avatar sx={{ bgcolor: theme?.palette?.primary?.light }}>
                <IoMdCard color={theme?.palette?.primary?.dark} />
              </Avatar>
              <Box>
                <Typography>No payment method connected</Typography>
                <Typography variant='body2' color='text.secondary' sx={{ display: { xs: 'none', sm: 'block' } }}>
                  Add a payment method to continue using our services
                </Typography>
              </Box>
            </Stack>
            <Button
              variant='contained'
              size='small'
              isPending={addingCard}
              startIcon={<IoMdCard />}
              sx={{ borderRadius: 5, width: { xs: '100%', sm: 'auto' } }}
              onClick={() => addCard()}
            >
              Connect Card
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

const PackagePricing = () => {
  const theme = useTheme();
  const { data: businessProfile, isLoading: BusinessDataLoading } = useGetBusinessProfile();
  const { data: businessPlan, isLoading } = useGetBusinessPlan(businessProfile?.business_plan_id);

  if (BusinessDataLoading || isLoading) return <PackagePricingSkeleton />;

  const rowStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.5
  } as const;

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, borderRadius: 2, height: '100%' }}>
      <Box pb={1.5} borderBottom='1px solid #ECECEC'>
        <Box display='flex' fontSize={18} flexDirection='row' alignItems='center' gap={1}>
          <IoMdCard color={theme.palette.primary.main} />
          <Typography variant='subtitle1' fontWeight={600} color='primary'>
            Current Package
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ display: { xs: 'none', sm: 'block' } }}>
          Your subscription details and features
        </Typography>
      </Box>
      <Box display='flex' flexDirection='column' mt={2}>
        <Box display='flex' justifyContent='space-between' mb={1.5} flexWrap='wrap' gap={1}>
          <Typography color='text.secondary'>Plan</Typography>
          <Typography
            sx={{
              bgcolor: 'primary.light',
              color: 'primary.main',
              px: '10px',
              py: '6px',
              borderRadius: 5,
              fontWeight: 500,
              fontSize: 14,
              lineHeight: 1
            }}
          >
            {businessPlan?.name}
          </Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Base Price</Typography>
          <Typography fontWeight={600} lineHeight={1.2}>
            {formatDollars(businessPlan?.base_price)}
          </Typography>
        </Box>

        <Box
          borderLeft='2px solid #E8E8E8'
          pl={2.5}
          ml={{ xs: 0.2, sm: 0.4 }}
          display='flex'
          flexDirection='column'
          gap={1.4}
          mb={2.2}
        >
          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included Minutes</Typography>
            <Typography fontWeight={600}>{businessPlan?.available_minutes ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included Warm Call Minutes</Typography>
            <Typography fontWeight={600}>{businessPlan?.available_warm_call_minutes ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included SMS Segments</Typography>
            <Typography fontWeight={600}>{businessPlan?.available_sms_segments ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included AI Tokens</Typography>
            <Typography fontWeight={600}>{businessPlan?.included_ai_tokens ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included AI Chat Messages</Typography>
            <Typography fontWeight={600}>{businessPlan?.available_chats ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included Locations</Typography>
            <Typography fontWeight={600}>{businessPlan?.included_locations ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included Concurrency Per Location</Typography>
            <Typography fontWeight={600}>{businessPlan?.included_concurrency_per_location ?? 0}</Typography>
          </Box>

          <Box sx={rowStyles}>
            <Typography color='text.secondary'>Included Storage</Typography>
            <Typography fontWeight={600}>{formatNumberDecimals(businessPlan?.included_storage ?? 0)} GB</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.8 }} />

        <Typography variant='body2' color='text.secondary' fontWeight={600} mb={1.8}>
          ADDITIONAL CHARGES IF EXCEEDED
        </Typography>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional Call Charge</Typography>
          <Typography fontWeight={600}>{formatDollars(businessPlan?.additional_per_min_charge, 9)}/min</Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional Warm Call Charge</Typography>
          <Typography fontWeight={600}>{formatDollars(businessPlan?.additional_warm_call_charge, 9)}/min</Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional SMS Segment Charge</Typography>
          <Typography fontWeight={600}>
            {formatDollars(businessPlan?.additional_per_sms_segment_charge, 9)}/segment
          </Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Cost per 1K Additional Tokens</Typography>
          <Typography fontWeight={600}>
            {formatDollars(businessPlan?.additional_per_1k_ai_tokens_charge, 9)}/1K tokens
          </Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional AI Chat Charge</Typography>
          <Typography fontWeight={600}>{formatDollars(businessPlan?.additional_per_chat_charge, 9)}/msg</Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional Storage Charge</Typography>
          <Typography fontWeight={600}>{formatDollars(businessPlan?.additional_per_storage_charge, 9)}/GB</Typography>
        </Box>

        <Box sx={rowStyles} mb={1.8}>
          <Typography color='text.secondary'>Additional Location Charge</Typography>
          <Typography fontWeight={600}>
            {formatDollars(businessPlan?.additional_per_location_charge, 9)}/location
          </Typography>
        </Box>

        {businessPlan?.price_details?.multilingual_charge_per_min?.enabled && (
          <Box sx={rowStyles} mb={1.8}>
            <Typography color='text.secondary'>Multilingual Charge</Typography>
            <Typography fontWeight={600}>
              {formatDollars(businessPlan.price_details?.multilingual_charge_per_min?.unit_amount, 9)}/min
            </Typography>
          </Box>
        )}

        {businessPlan?.price_details?.integration_charge?.enabled &&
          (businessPlan?.price_details?.integration_charge as any)?.unit_amount !== undefined && (
            <Box sx={rowStyles} mb={1.8}>
              <Typography color='text.secondary'>Integration Charge</Typography>
              <Typography fontWeight={600}>
                {formatDollars((businessPlan?.price_details?.integration_charge as any)?.unit_amount, 9)}/integration
              </Typography>
            </Box>
          )}

        {businessPlan?.price_details?.premium_support_per_location?.enabled && (
          <Box sx={rowStyles} mb={0.5}>
            <Typography color='text.secondary'>Premium Support Charge</Typography>
            <Typography fontWeight={600}>
              {formatDollars(businessPlan?.price_details?.premium_support_per_location?.unit_amount, 9)}/location
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const CurrentBill = ({ BillingData, isLoading, businessPlan }: any) => {
  const theme = useTheme();
  if (isLoading) return <CurrentBillSkeleton />;

  const smsUsage = BillingData?.sms_segment_usage;
  const smsExtra = smsUsage?.extra_usage ?? smsUsage?.quantity ?? 0;
  const smsUnitAmount = smsUsage?.unit_amount ?? businessPlan?.additional_per_sms_segment_charge;
  const smsEstimated = smsUsage?.estimated_amount ?? 0;

  const aiTokenUsage = BillingData?.ai_token_usage;
  const aiTokenBlocks = aiTokenUsage?.quantity ?? 0;
  const aiTokenUnitAmount = aiTokenUsage?.unit_amount ?? businessPlan?.additional_per_1k_ai_tokens_charge;
  const aiTokenEstimated = aiTokenUsage?.estimated_amount ?? 0;

  const getAppliedCouponDetails = () => {
    const couponDetails = BillingData?.coupon_discount;
    return (
      <Box display='flex' justifyContent='space-between' mb={2} color='#077F13'>
        {couponDetails.discount_type === 'percentage' ? (
          <Typography>Discount ({couponDetails.discount_value}% off)</Typography>
        ) : (
          <Typography>Discount ({formatDollars(couponDetails.discount_value)} off)</Typography>
        )}
        <Typography fontWeight={600}>-{formatDollars(couponDetails.discount_amount)}</Typography>
      </Box>
    );
  };

  return (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, lg: 2 }, borderRadius: 2, height: '100%' }}>
      {/* Header */}
      <Box mb={2} pb={1} borderBottom='1px solid #ECECEC'>
        <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
          <FaDollarSign color={theme.palette.primary.main} />
          <Typography variant='subtitle1' fontWeight={500} color='primary'>
            Current Month Bill
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ display: { xs: 'none', sm: 'block' } }}>
          {BillingData?.month} billing summary
        </Typography>
      </Box>

      {/* Base Price */}
      <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
        <Typography color='text.secondary'>Base Price</Typography>
        <Typography fontWeight={600}>{formatDollars(BillingData?.base_amount)}</Typography>
      </Box>

      {/* Extra Minutes */}
      {BillingData?.per_min_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional Call Minutes Charges
            <Typography component='span' color='black'>
              ({formatDuration(BillingData?.per_min_usage?.extra_usage)} x{' '}
              {formatDollars(BillingData?.per_min_usage?.unit_amount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.per_min_usage?.estimated_amount)}</Typography>
        </Box>
      )}

      {BillingData?.warm_call_per_min_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional Warm Call Minutes Charges
            <Typography component='span' color='black'>
              ({formatDuration(BillingData?.warm_call_per_min_usage?.extra_usage)} x{' '}
              {formatDollars(BillingData?.warm_call_per_min_usage?.unit_amount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>
            {formatDollars(BillingData?.warm_call_per_min_usage?.estimated_amount)}
          </Typography>
        </Box>
      )}

      {BillingData?.sms_segment_usage || businessPlan?.additional_per_sms_segment_charge != null ? (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional SMS Segment Charges{' '}
            <Typography component='span' color='black'>
              ({smsExtra} x {formatDollars(smsUnitAmount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(smsEstimated)}</Typography>
        </Box>
      ) : null}

      {BillingData?.ai_token_usage || businessPlan?.additional_per_1k_ai_tokens_charge != null ? (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional AI Token Charges{' '}
            <Typography component='span' color='black'>
              ({aiTokenBlocks} x {formatDollars(aiTokenUnitAmount, 9)}/1K)
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(aiTokenEstimated)}</Typography>
        </Box>
      ) : null}

      {BillingData?.per_chat_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional AI Chat Charges
            <Typography component='span' color='black'>
              ({BillingData?.per_chat_usage?.extra_usage} x {formatDollars(BillingData?.per_chat_usage?.unit_amount, 9)}
              )
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.per_chat_usage?.estimated_amount)}</Typography>
        </Box>
      )}

      {/* Storage */}
      {BillingData?.storage_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional Storage Charges{' '}
            <Typography component='span' color='black'>
              ({formatNumberDecimals(BillingData?.storage_usage?.quantity, 2)} GB x{' '}
              {formatDollars(BillingData?.storage_usage?.unit_amount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.storage_usage?.estimated_amount)}</Typography>
        </Box>
      )}

      {BillingData?.per_location_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Additional Location Charges{' '}
            <Typography component='span' color='black'>
              ({BillingData?.per_location_usage?.quantity} x{' '}
              {formatDollars(BillingData?.per_location_usage?.unit_amount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.per_location_usage?.estimated_amount)}</Typography>
        </Box>
      )}

      {/* Multilingual Charges */}
      {BillingData?.multilingual_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Multilingual Charges{' '}
            <Typography component='span' color='black'>
              ({formatDuration(BillingData?.multilingual_usage?.quantity)} x{' '}
              {formatDollars(BillingData?.multilingual_usage?.unit_amount, 9)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.multilingual_usage?.estimated_amount)}</Typography>
        </Box>
      )}

      {/* Integration Fee */}
      {BillingData?.integration_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary'>Integration Charges</Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.total_integration_amount)}</Typography>
        </Box>
      )}

      {/* Premium Support Per Location */}
      {BillingData?.premium_support_usage && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Premium Support Per Location{' '}
            <Typography component='span' color='black'>
              ({BillingData?.premium_support_usage?.quantity} x{' '}
              {formatDollars(BillingData?.premium_support_usage?.unit_amount)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>
            {formatDollars(BillingData?.premium_support_usage?.estimated_amount)}
          </Typography>
        </Box>
      )}

      {BillingData?.phone_number_usage && BillingData.phone_number_usage.estimated_amount > 0 && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary' sx={{ flex: { xs: '1 1 100%', sm: 'auto' } }}>
            Phone Number Charges{' '}
            <Typography component='span' color='black'>
              ({formatPhoneNumberChargesQuantityLabel(BillingData.phone_number_usage, formatDollars)})
            </Typography>
          </Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData.phone_number_usage.estimated_amount)}</Typography>
        </Box>
      )}

      {Number(BillingData?.total_addon_amount) > 0 && (
        <Box display='flex' justifyContent='space-between' mb={2} flexWrap='wrap' gap={0.5}>
          <Typography color='text.secondary'>Active Add-On Charges</Typography>
          <Typography fontWeight={600}>{formatDollars(BillingData?.total_addon_amount)}</Typography>
        </Box>
      )}

      {/* Applied Coupon */}
      {BillingData?.coupon_discount?.is_active && BillingData?.coupon_discount?.code && getAppliedCouponDetails()}

      {/* Total */}
      <Box
        display='flex'
        justifyContent='space-between'
        mt={1}
        pt={3}
        borderTop='1px solid #f0ebebff'
        flexWrap='wrap'
        gap={0.5}
      >
        <Typography color='text.secondary' fontWeight='bold'>
          Total
        </Typography>
        <Typography fontWeight='bold'>{formatDollars(BillingData?.total_amount)}</Typography>
      </Box>

      {/* Usage Progress */}
      <Box mt={1}>
        <UsageProgressCard fullWidth />
      </Box>
    </Paper>
  );
};

export default function BillingDetails() {
  const { data: businessProfile } = useGetBusinessProfile();
  const { data: businessPlan } = useGetBusinessPlan(businessProfile?.business_plan_id);
  const { data: BillingData, isLoading } = useGetBusinessBillingHistory();
  return (
    <Box mt={2}>
      <ApplyCoupon appliedCouponDetails={BillingData?.coupon_discount} isLoading={isLoading} />
      {businessProfile?.agency_billing_enabled && <PaymentMethod />}
      <Grid container spacing={{ xs: 2, lg: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PackagePricing />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CurrentBill BillingData={BillingData} isLoading={isLoading} businessPlan={businessPlan} />
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------- Skeleton Components ---------------- //
function PaymentMethodSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Skeleton variant='text' width={160} height={20} />
          <Skeleton variant='text' width={240} height={16} />
        </Box>
        <Card
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '12px',
            bgcolor: '#f9fafb',
            boxShadow: 'none',
            p: 2
          }}
        >
          <Box display='flex' alignItems='center' gap={2}>
            <Skeleton variant='circular' width={40} height={40} />
            <Box>
              <Skeleton variant='text' width={140} height={20} />
              <Skeleton variant='text' width={100} height={16} />
            </Box>
          </Box>
          <Skeleton variant='rounded' width={80} height={32} />
        </Card>
      </Stack>
    </Paper>
  );
}

function PackagePricingSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Box display='flex' flexDirection='column' gap={1.5}>
        <Skeleton variant='text' width={200} height={24} />
        <Skeleton variant='text' width={260} height={18} />

        {[1, 2, 3, 4, 5].map((i) => (
          <Box key={i} display='flex' justifyContent='space-between' mb={0.5}>
            <Skeleton variant='text' width={140} height={18} />
            <Skeleton variant='text' width={80} height={18} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function CurrentBillSkeleton() {
  return (
    <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
      <Box display='flex' flexDirection='column' gap={1}>
        <Skeleton variant='text' width={180} height={20} />
        <Skeleton variant='text' width={220} height={16} />

        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Box key={i} display='flex' justifyContent='space-between' mb={0.5}>
            <Skeleton variant='text' width={160} height={18} />
            <Skeleton variant='text' width={80} height={18} />
          </Box>
        ))}

        <Box display='flex' justifyContent='space-between' mt={1}>
          <Skeleton variant='text' width={60} height={20} />
          <Skeleton variant='text' width={80} height={20} />
        </Box>
      </Box>
    </Paper>
  );
}
